import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Network } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FeatureOff } from "../components/FeatureOff";
import { Empty, ErrorState, Loading } from "../components/States";
import { importedLinks, importSummary, isBusy, packageChoices, sheetDetailKey } from "../components/pid/flow";
import { PageView, ReadingPanel } from "../components/pid/PageView";
import { pidApi, type PidStatus, type Reading, type ReviewAction } from "../components/pid/pidApi";
import { RegisterTable } from "../components/pid/RegisterTable";
import { SheetList, UploadBox } from "../components/pid/SheetList";
import { apiGet, switchedOff } from "../lib/http";
import { useMe } from "../lib/me";
import { isUuid } from "../lib/params";

/** Why the screen cannot be used here, or null when it can. */
export function pidOffReason(status: PidStatus | undefined, error: unknown): string | null {
  return switchedOff(status) ?? switchedOff(error, [404]);
}

/** A line under the title saying which readers this deployment reads with. */
export function readerLine(status: PidStatus): string {
  const model = status.model_reader === "configured";
  const free = status.free_reader === "available";
  if (model && free) return "Sheets are read by the model reader and RapidOCR.";
  if (model) return "Sheets are read by the model reader only (RapidOCR is not installed).";
  if (free) return "Sheets are read by RapidOCR only (no model reader configured).";
  return "No reader is available on this deployment, so uploads will not be read.";
}

/** /pid: P&ID sets for a package, their readings, review, and the package tag register. */
export function Pid() {
  const { me } = useMe();
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const pkg = params.get("package") ?? "";
  const tab = params.get("tab") === "register" ? "register" : "sheets";
  // A sheet id from the address is used only if it is one: a bad link opens no sheet.
  const asked = params.get("sheet");
  const sheetId = isUuid(asked) ? asked : null;
  const [readingId, setReadingId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [imported, setImported] = useState<ReturnType<typeof importedLinks>>([]);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p);
  };

  const status = useQuery({ queryKey: ["pid", "status"], queryFn: pidApi.status });
  const listed = useQuery({ queryKey: ["documents", "packages"], queryFn: () => apiGet("/api/v1/documents/packages") });
  const choices = packageChoices(me, (listed.data ?? []).map((p) => p.name));
  useEffect(() => {
    if (!pkg && choices.length === 1) set({ package: choices[0] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg, choices.join("|")]);

  const sheets = useQuery({
    queryKey: ["pid", "sheets", pkg],
    queryFn: () => pidApi.sheets(pkg),
    enabled: !!pkg && status.data?.enabled === true,
    refetchInterval: (q) => ((q.state.data ?? []).some(isBusy) ? 2000 : false),
  });
  const detail = useQuery({
    queryKey: sheetDetailKey(sheetId, sheets.data),
    queryFn: () => pidApi.sheet(sheetId as string),
    enabled: !!sheetId && status.data?.enabled === true,
  });
  const register = useQuery({
    queryKey: ["pid", "register", pkg],
    queryFn: () => pidApi.register(pkg),
    enabled: !!pkg && tab === "register" && status.data?.enabled === true,
  });
  const found = useQuery({
    queryKey: ["pid", "find", pkg, query.trim()],
    queryFn: () => pidApi.find(pkg, query.trim()),
    enabled: !!pkg && tab === "register" && query.trim().length > 0,
  });

  const refresh = () => client.invalidateQueries({ queryKey: ["pid"] });
  const upload = useMutation({ mutationFn: (file: File) => pidApi.upload(pkg, file), onSettled: refresh });
  const readAgain = useMutation({ mutationFn: (id: string) => pidApi.readAgain(id), onSettled: refresh });
  const review = useMutation({
    mutationFn: (v: { reading: Reading; action: ReviewAction; value?: string; reason?: string }) =>
      pidApi.review(v.reading, v.action, v.value, v.reason),
    onMutate: () => setReviewError(null),
    onError: (e: Error) => setReviewError(e.message.includes("409") ? "Someone changed this reading; it has been reloaded." : e.message),
    onSettled: refresh,
  });
  const doImport = useMutation({
    mutationFn: () => pidApi.importToRegister(pkg),
    onSuccess: (r) => { setNote(importSummary(r)); setImported(importedLinks(r)); },
    onError: (e: Error) => { setNote(e.message); setImported([]); },
    onSettled: refresh,
  });

  const reading = useMemo(
    () => detail.data?.readings.find((r) => r.id === readingId) ?? null,
    [detail.data, readingId],
  );

  const off = pidOffReason(status.data, status.error ?? sheets.error);
  return (
    <div className="page">
      <h2 style={{ fontSize: 26, marginBottom: 6 }}>
        <span className="row"><Network size={24} aria-hidden="true" /> P&ID</span>
      </h2>
      <p className="muted" style={{ marginBottom: 16 }}>
        Upload drawing sets, check each reading against the drawing, and keep the package tag register.
        {status.data?.enabled && <> {readerLine(status.data)}</>}
      </p>

      {status.isPending && <Loading what="Checking P&ID reading" />}
      {off && <FeatureOff feature="P&ID reading" reason={off} />}
      {status.error && !off && <ErrorState error={status.error} onRetry={() => status.refetch()} />}

      {status.data?.enabled && (
        <>
          <div className="filters">
            <label htmlFor="pid-package" style={{ fontWeight: 600 }}>Package</label>
            <select id="pid-package" value={pkg} onChange={(e) => set({ package: e.target.value, sheet: null })}>
              <option value="">Choose a package</option>
              {choices.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {pkg && (
              <nav className="tabs" style={{ marginBottom: 0, borderBottom: 0 }} aria-label="P&ID views">
                <a href="#sheets" className={tab === "sheets" ? "active" : ""} aria-current={tab === "sheets" ? "page" : undefined} onClick={(e) => { e.preventDefault(); set({ tab: null }); }}>Sheets</a>
                <a href="#register" className={tab === "register" ? "active" : ""} aria-current={tab === "register" ? "page" : undefined} onClick={(e) => { e.preventDefault(); set({ tab: "register" }); }}>Tag register</a>
              </nav>
            )}
          </div>

          {!pkg && <Empty title="Choose a package">P&ID sets, readings and the tag register are kept per package.</Empty>}

          {pkg && tab === "sheets" && (
            <div className="stack">
              <UploadBox me={me} busy={upload.isPending} onUpload={(f) => upload.mutate(f)} />
              {upload.error && <p role="alert" style={{ color: "var(--color-fail)" }}>{upload.error.message}</p>}
              {upload.data && !upload.data.created && (
                <p role="status" className="muted">{upload.data.filename} was already uploaded to {pkg}; nothing was read again.</p>
              )}
              {sheets.isPending && <Loading what="Loading sheets" />}
              {sheets.error && !off && <ErrorState error={sheets.error} onRetry={() => sheets.refetch()} />}
              {sheets.data && sheets.data.length === 0 && (
                <Empty title={`No P&ID sets for ${pkg} yet`}>Upload a PDF to start.</Empty>
              )}
              {sheets.data && sheets.data.length > 0 && (
                <SheetList sheets={sheets.data} selected={sheetId} me={me}
                  onSelect={(id) => { setReadingId(null); set({ sheet: id }); }}
                  onReadAgain={(id) => readAgain.mutate(id)} />
              )}
              {sheetId && detail.isPending && <Loading what="Loading the sheet" />}
              {sheetId && detail.error && <ErrorState error={detail.error} onRetry={() => detail.refetch()} />}
              {detail.data && (
                <div className="viewer" style={{ gridTemplateColumns: "minmax(0, 1fr) 340px" }}>
                  <PageView detail={detail.data} selected={readingId} onSelect={setReadingId} />
                  {reading ? (
                    <ReadingPanel key={`${reading.id}:${reading.version}`} reading={reading} me={me}
                      busy={review.isPending} error={reviewError}
                      onReview={(action, value, reason) => review.mutate({ reading, action, value, reason })} />
                  ) : (
                    <Empty title={detail.data.readings.length ? "Choose a box on the drawing" : "Nothing was read on this sheet"}>
                      {detail.data.readings.length
                        ? `${detail.data.readings.length} readings. Yellow and red boxes are where the readers did not plainly agree.`
                        : "Read it again, or check the drawing is not blank."}
                    </Empty>
                  )}
                </div>
              )}
            </div>
          )}

          {pkg && tab === "register" && (
            <>
              {register.isPending && <Loading what="Loading the tag register" />}
              {register.error && <ErrorState error={register.error} onRetry={() => register.refetch()} />}
              {register.data && (
                <RegisterTable pkg={pkg} me={me} query={query} note={note} busy={doImport.isPending} imported={imported}
                  all={register.data.tags}
                  tags={query.trim() ? (found.data ?? []) : register.data.tags}
                  onQuery={setQuery}
                  onImport={() => doImport.mutate()}
                  onOpen={(sheet, rid) => { setReadingId(rid); set({ tab: null, sheet }); }} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
